import React, { useEffect, useMemo, useState } from "react";
import api from "../../utils/api";
import Sidebar from "../../components/Sidebar";
import {
    FiSearch,
    FiSliders,
    FiCheck,
    FiX,
    FiChevronDown,
    FiInbox,
    FiUsers,
    FiClock,
    FiTrendingUp,
    FiLoader,
} from "react-icons/fi";

const STATUS_META = {
    pending_payment: { label: "Pending", dot: "bg-amber-500", style: "bg-amber-50 text-amber-700 ring-amber-600/20" },
    active: { label: "Active", dot: "bg-emerald-500", style: "bg-emerald-50 text-emerald-700 ring-emerald-600/20" },
    rejected: { label: "Rejected", dot: "bg-red-500", style: "bg-red-50 text-red-700 ring-red-600/20" },
    expired: { label: "Expired", dot: "bg-slate-400", style: "bg-slate-100 text-slate-600 ring-slate-500/20" },
};

const StatusBadge = ({ status }) => {
    const meta = STATUS_META[status] || STATUS_META.expired;
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset ${meta.style}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
            {meta.label}
        </span>
    );
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${accent}`}>
            <Icon size={16} />
        </div>
        <div>
            <p className="text-lg font-bold leading-none text-[#051948]">{value}</p>
            <p className="mt-1 text-[11px] font-medium text-slate-400">{label}</p>
        </div>
    </div>
);

const Subscriptions = () => {
    const [tab, setTab] = useState("requests");

    const [requests, setRequests] = useState([]);
    const [requestsLoading, setRequestsLoading] = useState(true);
    const [requestsError, setRequestsError] = useState(null);
    const [actioningId, setActioningId] = useState(null);

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [billingFilter, setBillingFilter] = useState("all");
    const [filtersOpen, setFiltersOpen] = useState(false);

    const [pricing, setPricing] = useState(null);
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (tab === "pricing") loadPricing();
        if (tab === "requests") loadRequests();
    }, [tab]);

    const loadRequests = async () => {
        setRequestsLoading(true);
        setRequestsError(null);
        try {
            const { data } = await api.get("/subscriptions");
            setRequests(data);
        } catch (err) {
            setRequestsError(err.response?.data?.message || "Erreur de chargement");
        } finally {
            setRequestsLoading(false);
        }
    };

    const handleActivate = async (id) => {
        setActioningId(id);
        try {
            await api.patch(`/subscriptions/${id}/activate`);
            setRequests((prev) =>
                prev.map((r) => (r._id === id ? { ...r, status: "active" } : r))
            );
        } catch (err) {
            setRequestsError(err.response?.data?.message || "Erreur");
        } finally {
            setActioningId(null);
        }
    };

    const handleReject = async (id) => {
        const reason = window.prompt("Raison du rejet (optionnel) :") || "";
        setActioningId(id);
        try {
            await api.patch(`/subscriptions/${id}/reject`, { reason });
            setRequests((prev) =>
                prev.map((r) => (r._id === id ? { ...r, status: "rejected" } : r))
            );
        } catch (err) {
            setRequestsError(err.response?.data?.message || "Erreur");
        } finally {
            setActioningId(null);
        }
    };

    const loadPricing = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get("/subscriptions/pricing-info");
            setPricing(data.pricing);
            setFeatures(data.features);
        } catch (err) {
            setError(err.response?.data?.message || "Erreur de chargement");
        } finally {
            setLoading(false);
        }
    };

    const savePricing = async () => {
        setSaving(true);
        setError(null);
        try {
            const { data } = await api.put("/subscriptions/admin/pricing", {
                singleEventPrice: pricing.singleEventPrice,
                annualPrice: pricing.annualPrice,
                annualEventPrice: pricing.annualEventPrice,
                standsIncluded: pricing.standsIncluded,
                standStepCost: pricing.standStepCost,
            });
            setPricing(data);
        } catch (err) {
            setError(err.response?.data?.message || "Erreur de sauvegarde");
        } finally {
            setSaving(false);
        }
    };

    const saveFeaturePrice = async (key, price) => {
        setSaving(true);
        setError(null);
        try {
            const { data } = await api.put(`/subscriptions/admin/features/${key}`, { price });
            setFeatures((prev) => prev.map((f) => (f.key === key ? data : f)));
        } catch (err) {
            setError(err.response?.data?.message || "Erreur de sauvegarde");
        } finally {
            setSaving(false);
        }
    };

    const initials = (name = "") =>
        name
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((n) => n[0]?.toUpperCase())
            .join("");

    const filteredRequests = useMemo(() => {
        const q = search.trim().toLowerCase();
        return requests.filter((r) => {
            if (statusFilter !== "all" && r.status !== statusFilter) return false;
            if (billingFilter !== "all" && r.billingMode !== billingFilter) return false;
            if (!q) return true;
            const haystack = `${r.user?.name || ""} ${r.user?.email || ""} ${r.user?.phone || ""}`.toLowerCase();
            return haystack.includes(q);
        });
    }, [requests, search, statusFilter, billingFilter]);

    const stats = useMemo(() => {
        const pending = requests.filter((r) => r.status === "pending_payment").length;
        const active = requests.filter((r) => r.status === "active").length;
        const revenue = requests
            .filter((r) => r.status === "active")
            .reduce((sum, r) => sum + (r.totalPrice || 0), 0);
        return { total: requests.length, pending, active, revenue };
    }, [requests]);

    const activeFilterCount = (statusFilter !== "all" ? 1 : 0) + (billingFilter !== "all" ? 1 : 0);

    return (
        <div className="flex min-h-screen bg-slate-50">
            <Sidebar />

            <div className="flex-1 p-6 lg:p-8">
                {/* Header */}
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight text-[#051948]">Subscriptions</h1>
                        <p className="mt-1 text-sm text-slate-400">
                            Manage client subscription requests and platform pricing.
                        </p>
                    </div>
                </div>

                {/* Segmented tabs */}
                <div className="mt-6 inline-flex rounded-xl border border-slate-200 bg-white p-1">
                    <button
                        onClick={() => setTab("requests")}
                        className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === "requests" ? "bg-[#051948] text-white" : "text-slate-500 hover:text-[#051948]"
                            }`}
                    >
                        Requests
                    </button>
                    <button
                        onClick={() => setTab("pricing")}
                        className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === "pricing" ? "bg-[#051948] text-white" : "text-slate-500 hover:text-[#051948]"
                            }`}
                    >
                        Pricing
                    </button>
                </div>

                {tab === "requests" && (
                    <div className="mt-6">
                        {/* Stats */}
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                            <StatCard icon={FiInbox} label="Total requests" value={stats.total} accent="bg-blue-50 text-blue-600" />
                            <StatCard icon={FiClock} label="Pending" value={stats.pending} accent="bg-amber-50 text-amber-600" />
                            <StatCard icon={FiUsers} label="Active" value={stats.active} accent="bg-emerald-50 text-emerald-600" />
                            <StatCard
                                icon={FiTrendingUp}
                                label="Active revenue"
                                value={`${stats.revenue.toLocaleString()} MAD`}
                                accent="bg-[#051948]/5 text-[#051948]"
                            />
                        </div>

                        {/* Search + filters */}
                        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                            <div className="relative flex-1">
                                <FiSearch size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search by name, email or phone..."
                                    className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm text-[#051948] placeholder:text-slate-400 focus:border-[#051948]/30 focus:outline-none focus:ring-2 focus:ring-[#051948]/10"
                                />
                            </div>

                            <div className="relative">
                                <button
                                    onClick={() => setFiltersOpen((v) => !v)}
                                    className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${activeFilterCount > 0
                                            ? "border-[#051948]/20 bg-[#051948]/5 text-[#051948]"
                                            : "border-slate-200 bg-white text-slate-500 hover:text-[#051948]"
                                        }`}
                                >
                                    <FiSliders size={14} />
                                    Filters
                                    {activeFilterCount > 0 && (
                                        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#051948] text-[10px] font-bold text-white">
                                            {activeFilterCount}
                                        </span>
                                    )}
                                    <FiChevronDown size={14} className={`transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
                                </button>

                                {filtersOpen && (
                                    <div className="absolute right-0 z-10 mt-2 w-64 rounded-xl border border-slate-200 bg-white p-4 shadow-lg shadow-slate-200/60">
                                        <div>
                                            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Status</p>
                                            <div className="mt-2 flex flex-wrap gap-1.5">
                                                {["all", "pending_payment", "active", "rejected", "expired"].map((s) => (
                                                    <button
                                                        key={s}
                                                        onClick={() => setStatusFilter(s)}
                                                        className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold capitalize transition-colors ${statusFilter === s
                                                                ? "bg-[#051948] text-white"
                                                                : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                                                            }`}
                                                    >
                                                        {s === "all" ? "All" : STATUS_META[s]?.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="mt-4">
                                            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Billing mode</p>
                                            <div className="mt-2 flex flex-wrap gap-1.5">
                                                {["all", "single", "annual"].map((b) => (
                                                    <button
                                                        key={b}
                                                        onClick={() => setBillingFilter(b)}
                                                        className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold capitalize transition-colors ${billingFilter === b
                                                                ? "bg-[#051948] text-white"
                                                                : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                                                            }`}
                                                    >
                                                        {b === "all" ? "All" : b}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {activeFilterCount > 0 && (
                                            <button
                                                onClick={() => {
                                                    setStatusFilter("all");
                                                    setBillingFilter("all");
                                                }}
                                                className="mt-4 flex w-full items-center justify-center gap-1 rounded-lg border border-slate-200 py-1.5 text-[11px] font-semibold text-slate-500 hover:text-red-500"
                                            >
                                                <FiX size={12} />
                                                Clear filters
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Content */}
                        <div className="mt-4">
                            {requestsLoading && (
                                <div className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-16 text-sm text-slate-400">
                                    <FiLoader size={16} className="animate-spin" />
                                    Loading requests...
                                </div>
                            )}

                            {requestsError && (
                                <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
                                    {requestsError}
                                </p>
                            )}

                            {!requestsLoading && filteredRequests.length === 0 && (
                                <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center">
                                    <FiInbox size={22} className="text-slate-300" />
                                    <p className="text-sm font-semibold text-slate-500">No requests found</p>
                                    <p className="text-xs text-slate-400">
                                        {requests.length === 0 ? "No subscription requests yet." : "Try adjusting your search or filters."}
                                    </p>
                                </div>
                            )}

                            {!requestsLoading && filteredRequests.length > 0 && (
                                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-b border-slate-200 bg-slate-50/60 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                                                    <th className="px-4 py-3">Client</th>
                                                    <th className="px-4 py-3">Plan</th>
                                                    <th className="px-4 py-3">Modules</th>
                                                    <th className="px-4 py-3">Prix</th>
                                                    <th className="px-4 py-3">Statut</th>
                                                    <th className="px-4 py-3 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredRequests.map((r) => (
                                                    <tr key={r._id} className="border-b border-slate-100 transition-colors last:border-0 hover:bg-slate-50/60">
                                                        <td className="px-4 py-3">
                                                            <div className="flex items-center gap-3">
                                                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#051948]/5 text-[11px] font-bold text-[#051948]">
                                                                    {initials(r.user?.name) || "?"}
                                                                </div>
                                                                <div>
                                                                    <p className="font-semibold text-[#051948]">{r.user?.name}</p>
                                                                    <p className="text-xs text-slate-400">{r.user?.email}</p>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <p className="text-[#051948]">
                                                                {r.billingMode === "annual" ? `Annual · ${r.eventsCount} events` : "Single event"}
                                                            </p>
                                                            <p className="text-xs text-slate-400">{r.stands} stands</p>
                                                        </td>
                                                        <td className="px-4 py-3 text-xs text-slate-500">
                                                            {r.modules?.length ? r.modules.join(", ") : "—"}
                                                        </td>
                                                        <td className="px-4 py-3 font-semibold text-[#051948]">
                                                            {r.totalPrice} {r.currency}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <StatusBadge status={r.status} />
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {r.status === "pending_payment" ? (
                                                                <div className="flex justify-end gap-2">
                                                                    <button
                                                                        onClick={() => handleActivate(r._id)}
                                                                        disabled={actioningId === r._id}
                                                                        className="flex items-center gap-1 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                                                                    >
                                                                        <FiCheck size={12} />
                                                                        Activer
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleReject(r._id)}
                                                                        disabled={actioningId === r._id}
                                                                        className="flex items-center gap-1 rounded-lg bg-red-500 px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                                                                    >
                                                                        <FiX size={12} />
                                                                        Rejeter
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <span className="flex justify-end text-xs text-slate-300">—</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {tab === "pricing" && (
                    <div className="mt-6 max-w-xl">
                        {loading && (
                            <div className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-16 text-sm text-slate-400">
                                <FiLoader size={16} className="animate-spin" />
                                Loading pricing...
                            </div>
                        )}
                        {error && (
                            <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600">{error}</p>
                        )}

                        {!loading && pricing && (
                            <>
                                <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6">
                                    <h2 className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                                        Base pricing
                                    </h2>

                                    <div className="grid grid-cols-2 gap-4">
                                        <label className="text-xs font-semibold text-slate-500">
                                            Single event price
                                            <input
                                                type="number"
                                                value={pricing.singleEventPrice}
                                                onChange={(e) =>
                                                    setPricing({ ...pricing, singleEventPrice: Number(e.target.value) })
                                                }
                                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-[#051948] focus:border-[#051948]/30 focus:outline-none focus:ring-2 focus:ring-[#051948]/10"
                                            />
                                        </label>
                                        <label className="text-xs font-semibold text-slate-500">
                                            Annual platform fee (per year)
                                            <input
                                                type="number"
                                                value={pricing.annualPrice ?? 0}
                                                onChange={(e) =>
                                                    setPricing({ ...pricing, annualPrice: Number(e.target.value) })
                                                }
                                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-[#051948] focus:border-[#051948]/30 focus:outline-none focus:ring-2 focus:ring-[#051948]/10"
                                            />
                                        </label>
                                        <label className="text-xs font-semibold text-slate-500">
                                            Annual price per event
                                            <input
                                                type="number"
                                                value={pricing.annualEventPrice}
                                                onChange={(e) =>
                                                    setPricing({ ...pricing, annualEventPrice: Number(e.target.value) })
                                                }
                                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-[#051948] focus:border-[#051948]/30 focus:outline-none focus:ring-2 focus:ring-[#051948]/10"
                                            />
                                        </label>
                                        <label className="text-xs font-semibold text-slate-500">
                                            Stands included
                                            <input
                                                type="number"
                                                value={pricing.standsIncluded}
                                                onChange={(e) =>
                                                    setPricing({ ...pricing, standsIncluded: Number(e.target.value) })
                                                }
                                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-[#051948] focus:border-[#051948]/30 focus:outline-none focus:ring-2 focus:ring-[#051948]/10"
                                            />
                                        </label>
                                        <label className="text-xs font-semibold text-slate-500">
                                            Stand step cost
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={pricing.standStepCost}
                                                onChange={(e) =>
                                                    setPricing({ ...pricing, standStepCost: Number(e.target.value) })
                                                }
                                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-[#051948] focus:border-[#051948]/30 focus:outline-none focus:ring-2 focus:ring-[#051948]/10"
                                            />
                                        </label>
                                    </div>

                                    <button
                                        onClick={savePricing}
                                        disabled={saving}
                                        className="rounded-lg bg-[#051948] px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                                    >
                                        {saving ? "Saving..." : "Save base pricing"}
                                    </button>
                                </div>

                                <div className="mt-6 space-y-3 rounded-2xl border border-slate-200 bg-white p-6">
                                    <h2 className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                                        Modules
                                    </h2>

                                    {features.map((f) => (
                                        <div key={f.key} className="flex items-center justify-between gap-4">
                                            <span className="text-sm font-medium text-[#051948]">{f.name}</span>
                                            <input
                                                type="number"
                                                value={f.price}
                                                onChange={(e) =>
                                                    setFeatures((prev) =>
                                                        prev.map((x) =>
                                                            x.key === f.key ? { ...x, price: Number(e.target.value) } : x
                                                        )
                                                    )
                                                }
                                                onBlur={(e) => saveFeaturePrice(f.key, Number(e.target.value))}
                                                className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm text-[#051948] focus:border-[#051948]/30 focus:outline-none focus:ring-2 focus:ring-[#051948]/10"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Subscriptions;